import type { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import type { RouteParams } from '../routes/router';
import { SAGA_ACTIONS } from '../sagas/sagaActions';
import {
  handleCustomerInfo,
  watchSetBillingForm,
} from '../shared/billing/billingSagas';
import { DEFAULT_LANGUAGE } from '../shared/languages';
import {
  getCountries,
  getCustomerInfo,
  getItemCatalogData,
  getLocalization,
  getSession,
  getTrialDescription,
} from '../shared/requestSagas';
import {
  setCountries,
  setEligibility,
  setError,
  setLocalization,
  setSession,
} from '../store/appSlice';
import {
  clearForm,
  clearInfo,
  setForm as setBillingForm,
} from '../store/billingSlice';
import { selectLanguage } from '../store/routingSelectors';
import {
  resetTrial,
  setDescription,
  setInitialized,
  setLoading,
  setSourceCatalog,
  setTrialCatalog,
} from '../store/trialSlice';
import { onTrialInit, onTrialLogin } from '../utils/gtm';
import { handleValidateEmail } from '../utils/validators/validators';
import { watchTrialPayment } from './trialOrderSagas';

export function* navigateTrial(params: RouteParams): SagaIterator<void> {
  yield fork(watchCheckEmail);
  yield fork(watchSetBillingForm);
  yield fork(watchTrialPayment);

  const trialName = params?.trialName;
  if (typeof trialName !== 'string' || !trialName) {
    yield put(setError(''));
    return;
  }

  yield fork(watchLoginSuccess, trialName);
  yield call(initTrial, trialName);
}

function* watchCheckEmail() {
  yield takeLatest(SAGA_ACTIONS.CHECK_EMAIL, handleValidateEmail);
}

function* watchLoginSuccess(trialName: string) {
  yield takeLatest(SAGA_ACTIONS.LOGIN_SUCCESS, handleLoginSuccess, trialName);
}

function* handleLoginSuccess(trialName: string): SagaIterator<void> {
  yield call(loadTrial, trialName);

  const { session, localization } = yield select((state) => state.app);
  const { trialCatalog, sourceCatalog } = yield select((state) => state.trial);
  const { info } = yield select((state) => state.billing);

  if (trialCatalog) {
    onTrialLogin({
      trialCatalog,
      sourceCatalog,
      localization,
      session,
      email: session?.Email,
      billingInfo: info,
    });
  }
}

function* initTrial(trialName: string): SagaIterator<void> {
  yield call(loadTrial, trialName);

  const { localization } = yield select((state) => state.app);
  const { trialCatalog, sourceCatalog } = yield select((state) => state.trial);

  if (trialCatalog) {
    onTrialInit({ trialCatalog, sourceCatalog, localization });
  }
}

function* loadTrial(trialName: string): SagaIterator<void> {
  yield put(resetTrial());
  yield put(setLoading(true));

  const session = yield call(getSession);
  yield put(setSession(session));

  yield put(clearForm());
  yield put(clearInfo());
  yield put(setEligibility(null));
  yield put(setError(null));

  yield put(setBillingForm({ country: session?.Location }));

  const countries = yield call(getCountries);
  yield put(setCountries(countries));

  const localization = yield call(getLocalization);
  yield put(setLocalization(localization));

  if (session?.Email) {
    const customerInfo = yield call(getCustomerInfo);
    yield call(handleCustomerInfo, customerInfo);
  }

  try {
    const cms = yield call(getTrialDescription, trialName);
    const language = yield select(selectLanguage);
    const description =
      cms?.descriptions?.[language] ?? cms?.descriptions?.[DEFAULT_LANGUAGE];

    if (!description?.product?.ratePlan) {
      throw new Error(`No trial description for "${trialName}"`);
    }

    yield put(setDescription(description));

    const [trialCatalog, sourceCatalog] = yield all([
      call(getItemCatalogData, description.product.ratePlan),
      call(getItemCatalogData, description.product.sourceRatePlan),
    ]);

    yield put(setTrialCatalog(trialCatalog));
    yield put(setSourceCatalog(sourceCatalog));
  } catch (error) {
    yield put(setError('', error, { trialName }));
  }

  yield put(setInitialized(true));
  yield put(setLoading(false));
}
